import React, { useState, useEffect } from 'react';
import './Adminapprovals.css';
import { getBackendUrl } from '../../../api';
import Navbar from '../Navbar/Navbar.jsx';
import TopBar from '../Navbar/Mainbar.jsx';
import Swal from 'sweetalert2'; // ✨ Added Swal Import

/* ─── helpers ─────────────────────────────────────────────── */
const fmt = (dateStr) => {
  if (!dateStr) return '';
  try {
    if (dateStr.includes('/')) return dateStr;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-GB');
  } catch { return dateStr; }
};

const leaveLabel = (type) => {
  if (!type) return 'Unknown';
  const m = { casual:'Casual Leave', sick:'Medical Leave', medical:'Medical Leave',
    emergency:'Permission Leave', permission:'Permission Leave', annual:'Annual Leave',
    maternity:'Maternity Leave', paternity:'Paternity Leave', unpaid:'Unpaid Leave' };
  return m[type.toLowerCase()] || type;
};

const priority = (req) => {
  const days = Math.ceil((new Date(req.startDateISO || req.startDate || req.start_date) - new Date()) / 86400000);
  return days <= 1 ? 'high' : days <= 3 ? 'medium' : 'low';
};

const initials = (name) => name ? name.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2) : 'A';
const fmtNum = (n) => (n === null || n === undefined || isNaN(n)) ? '0' : Number(n).toFixed(1);

const TYPE_FILTER_ALIASES = {
  'Earned Leave': ['casual', 'earned'],
};

/* ─── component ───────────────────────────────────────────── */
const Approvals = () => {
  const [search,      setSearch]      = useState('');
  const [filterType,  setFilterType]  = useState('All Types');
  const [dateFrom,    setDateFrom]    = useState('');
  const [dateTo,      setDateTo]      = useState('');
  const [modalOpen,   setModalOpen]   = useState(false);
  const [comment,     setComment]     = useState('');
  const [selItem,     setSelItem]     = useState(null);
  const [cards,       setCards]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  const [processingItem, setProcessingItem] = useState(null);
  const isAnyProcessing = processingItem !== null;

  const [picCache,    setPicCache]    = useState({});
  const [mgrInfo,     setMgrInfo]     = useState({ managerId: null });

  const [currentUser, setCurrentUser] = useState(null);
  const [profilePic,  setProfilePic]  = useState(null);

  const BACKEND = getBackendUrl();
  const go = (path, opts={}) => fetch(`${BACKEND}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type':'application/json', 'Accept':'application/json' },
    ...opts
  });

  /* ── profile picture ──────────────────────────────────── */
  const getPic = async (email, name) => {
    const key = email || name;
    if (picCache[key]) return picCache[key];
    try {
      const r = await go('/api/employee-list');
      if (r.ok) {
        const list = await r.json();
        const emp  = list.find(e => e.email === email || e.name === name);
        const pic  = emp?.profile_picture || '/Images/Manurevalogo.png';
        setPicCache(p => ({ ...p, [key]: pic }));
        return pic;
      }
    } catch {}
    return '/Images/Manurevalogo.png';
  };

  /* ── medical doc ─────────────────────────────────────── */
  const getDoc = (req) => {
    const hits = [
      req.has_document, req.certificate, req.has_medical_document,
      req.document_filename && req.document_filename !== 'null',
      req.certificateFileName && req.certificateFileName !== 'null',
      req.document_path && req.document_path !== 'null',
      req.certificatePath, req.file_path, req.certificateUrl, req.document_url
    ].filter(Boolean).length;

    if (hits > 0) {
      const name = [req.document_filename, req.certificateFileName, req.medical_document_filename, req.original_filename]
        .find(n => n && n !== 'null') || 'Medical Certificate';
      const url = [req.certificateUrl, req.document_url].find(u => u) || `/api/download-medical-document/${req.id}`;
      return { hasDoc: true, url, name };
    }
    return { hasDoc: false };
  };

  /* ── download ────────────────────────────────────────── */
  const download = async (url, name) => {
    try {
      const r = await fetch(url, { method:'GET', credentials:'include', headers:{ 'Accept':'application/pdf,*/*' } });
      if (!r.ok) { 
        Swal.fire({
          title: 'Download Failed',
          text: `Could not download document (Status: ${r.status})`,
          icon: 'error',
          iconColor: '#EF4444',
          width: '360px',
          confirmButtonColor: '#374151',
          customClass: { popup: 'rounded-2xl shadow-xl' }
        });
        return; 
      }
      const blob = await r.blob();
      if (!blob.size) { 
        Swal.fire({
          title: 'Empty File',
          text: 'The document data appears to be empty.',
          icon: 'warning',
          iconColor: '#F59E0B',
          width: '360px',
          confirmButtonColor: '#374151',
          customClass: { popup: 'rounded-2xl shadow-xl' }
        });
        return; 
      }
      const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: name || 'document.pdf' });
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch { 
      Swal.fire({
        title: 'Connection Error',
        text: 'Network issue caused the download to fail.',
        icon: 'error',
        iconColor: '#EF4444',
        width: '360px',
        confirmButtonColor: '#374151',
        customClass: { popup: 'rounded-2xl shadow-xl' }
      });
    }
  };

  /* ── balance ─────────────────────────────────────────── */
  const getBalance = async (empId, email, reqId) => {
    try {
      let id = empId;
      if (!id && email) {
        const r = await go('/api/employee-list');
        if (r.ok) id = (await r.json()).find(e => e.email?.toLowerCase() === email.toLowerCase())?.id;
      }
      if (!id) return 'Casual: --/12 | Medical: --/15';

      const r = await go(`/api/employee/${id}/leave-balance${reqId ? `?request_id=${reqId}` : ''}`);
      if (!r.ok) return 'Casual: --/12 | Medical: --/15';

      const d = await r.json();
      const b = d.balances || d || {};
      const c = b.casual || b.CASUAL || b.Casual || {};
      const m = b.medical || b.SICK || b.sick || b.Medical || {};

      const cUsed = parseFloat(c.used||0), cPend = parseFloat(c.pending||0), cAlloc = parseFloat(c.allocated||12);
      const isLop = Boolean(c.is_lop), lopDays = parseFloat(c.lop_days||0);
      const mUsed = parseFloat(m.used||0), mPend = parseFloat(m.pending||0), mAlloc = parseFloat(m.allocated||15);

      let txt = `Casual: ${fmtNum(cUsed+cPend)}/${fmtNum(cAlloc)}`;
      if (isLop && lopDays > 0) txt += ` [LOP: ${fmtNum(lopDays)}]`;
      return `${txt} | Medical: ${fmtNum(mUsed+mPend)}/${fmtNum(mAlloc)}`;
    } catch { return 'Casual: --/12 | Medical: --/15'; }
  };

  /* ── fetch cards ─────────────────────────────────────── */
  const load = async () => {
    setLoading(true); setError(null);
    try {
      let mgr = mgrInfo;
      if (!mgr.managerId) {
        const ur = await go('/api/user');
        if (ur.ok) { const u = await ur.json(); mgr = { managerId: u.id }; setMgrInfo(mgr); }
      }

      const r = await go('/api/manager/pending-team-approvals');
      if (!r.ok) throw new Error(`Server error ${r.status}`);
      const data = await r.json();
      if (!Array.isArray(data)) throw new Error('Invalid response');

      const built = await Promise.all(data.map(async (req) => {
        const email  = req.employeeEmail || req.employee_email || '';
        const name   = req.employeeName  || req.employee_name  || 'Unknown';
        const empId  = req.employeeId    || req.employee_id    || req.userId || req.user_id;
        const days   = req.daysRequested || req.days_requested || 1;
        const isHalf = req.is_half_day   || req.isHalfDay      || false;
        const lt     = (req.leaveType    || req.leave_type     || '').toLowerCase();
        const isPerm = lt === 'permission' || lt === 'emergency';
        const unit   = isPerm ? 'hour' : 'day';

        const [pic, balance] = await Promise.all([
          getPic(email, name),
          getBalance(empId, email, req.id)
        ]);
        const doc = getDoc(req);

        return {
          id:          req.id,
          pic,
          name,        email,
          type:        leaveLabel(req.leaveType || req.leave_type),
          duration:    isHalf && days === 0.5 ? 'Half Day (0.5)' : days === 1 ? `1 ${unit}` : `${days} ${unit}s`,
          dates:       req.dateRange || (req.startDate && req.endDate
                         ? req.startDate === req.endDate
                           ? fmt(req.startDate)
                           : `${fmt(req.startDate)} – ${fmt(req.endDate)}`
                         : 'Not specified'),
          balance,
          reason:      req.reason || 'No reason provided',
          hasDoc:      doc.hasDoc,
          docUrl:      doc.url,
          docName:     doc.name,
          prio:        priority(req),
          date:        fmt(req.dateApplied || req.createdAt),
          isHalf,
          halfPeriod:  req.half_day_period || req.halfDayPeriod || null,
          isTeam:      req.assigned_manager_id === mgr.managerId,
          isCancelReq: req.is_cancel_request === true || req.status === 'Cancel Requested',
          orig:        { ...req, employeeId: empId, employee_id: empId, days_requested: parseFloat(days) },
        };
      }));
      setCards(built);
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();

    go('/api/user')
      .then(r => r.ok ? r.json() : null)
      .then(u => { if (u) setCurrentUser(u); })
      .catch(() => {});

    go('/api/user/profile-picture')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.profile_picture) setProfilePic(d.profile_picture); })
      .catch(() => {});

    const h = () => load();
    window.addEventListener('leaveBalanceAdjusted', h);
    window.addEventListener('globalConfigChanged', h);
    return () => {
      window.removeEventListener('leaveBalanceAdjusted', h);
      window.removeEventListener('globalConfigChanged', h);
    };
  }, []);

  /* ── approve / reject — normal leave ─────────────────── */
  const doAction = async (action, id, comments='') => {
    setProcessingItem({ id, action });
    try {
      const r = await go(`/api/approve-leave/${id}`, {
        method: 'POST',
        body: JSON.stringify({ action, comments })
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Failed to ${action}`);
      }
      setCards(prev => prev.filter(c => c.id !== id));
      window.dispatchEvent(new CustomEvent(
        `leaveRequest${action === 'approve' ? 'Approved' : 'Rejected'}`,
        { detail: { requestId: id } }
      ));
      setTimeout(load, 1000);

      // ✨ Custom success pop-up
      Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#f0fdf4;border:1.5px solid #bbf7d0;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">
            ${action === 'approve' ? 'Approved' : 'Rejected'}
          </p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">
            Leave request has been successfully ${action}ed.
          </p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'OK',
      confirmButtonColor: '#111111',
      customClass: {
        popup: 'swal-employee rounded-2xl',
        confirmButton: 'rounded-sm text-sm font-medium px-6',
      }
    });
    } catch(e) {
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Action failed</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">Failed to ${action}: ${e.message}</p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'Close',
      confirmButtonColor: '#111111',
      customClass: {
        popup: 'swal-employee rounded-2xl',
        confirmButton: 'rounded-sm text-sm font-medium px-6',
      }
    });
    } finally {
      setProcessingItem(null);
    }
  };

  /* ── approve / reject — cancellation request ─────────── */
  const doCancelAction = async (action, id, comments = '') => {
    setProcessingItem({ id, action });
try {
  const r = await go(`/api/admin/leave-requests/${id}/approve-cancel`, {
    method: 'POST',
    body: JSON.stringify({ action, comments })
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || `Failed to ${action} cancellation`);
  }
  setCards(prev => prev.filter(c => c.id !== id));
  setTimeout(load, 1000);

  Swal.fire({
    html: `
      <div style="padding: 1rem 0.5rem">
        <div style="width:56px;height:56px;border-radius:50%;background:#f0fdf4;border:1.5px solid #bbf7d0;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">
          ${action === 'approve' ? 'Cancellation saved' : 'Request closed'}
        </p>
        <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">
          ${action === 'approve'
            ? 'Cancellation approved! Leave balance has been restored.'
            : 'Cancellation rejected. Leave remains approved.'}
        </p>
      </div>
    `,
    width: '340px',
    confirmButtonText: 'OK',
    confirmButtonColor: '#111111',
    customClass: {
      popup: 'swal-employee rounded-2xl',
      confirmButton: 'rounded-sm text-sm font-medium px-6',
    }
  });

} catch (e) {
  Swal.fire({
    html: `
      <div style="padding: 1rem 0.5rem">
        <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </div>
        <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Error</p>
        <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">Failed: ${e.message}</p>
      </div>
    `,
    width: '340px',
    confirmButtonText: 'Close',
    confirmButtonColor: '#111111',
    customClass: {
      popup: 'swal-employee rounded-2xl',
      confirmButton: 'rounded-sm text-sm font-medium px-6',
    }
  });

} finally {
  setProcessingItem(null);
}
  };

  /* ── approve normal leave ────────────────────────────── */
  const approve = async (item) => {
    const empId = item.orig?.employeeId || item.orig?.employee_id;
    let warnHtml = '';
    
    if (item.type.toLowerCase().includes('casual') && empId) {
      try {
        const r = await go(`/api/employee/${empId}/leave-balance?request_id=${item.id}`);
        if (r.ok) {
          const d     = await r.json();
          const isLop = d.balances?.casual?.is_lop || false;
          const lopD  = parseFloat(d.balances?.casual?.lop_days || 0);
          if (isLop && lopD > 0) {
            const rem = parseFloat(d.balances?.casual?.remaining || 0).toFixed(1);
            const req = parseFloat(item.orig?.days_requested || 1).toFixed(1);
            // Formatted safely to look premium inside the message field
            warnHtml = `<div style="text-align: left; background:#fef2f2; border:1px solid #fca5a5; padding:10px; border-radius:8px; font-size:12px; color:#991b1b; margin-top:10px;">
              <strong>⚠️ Loss of Pay (LOP) Warning:</strong><br/>
              Balance: ${rem} days | Requested: ${req} days<br/>
              <strong>${lopD.toFixed(1)} LOP days</strong> will be deducted from their salary.
            </div>`;
          }
        }
      } catch {}
    }

    // ✨ Rich Confirmation Popups replacing standard prompt window
  const result = await Swal.fire({
  html: `
    <div style="padding: 1rem 0.5rem">
      <div style="width:56px;height:56px;border-radius:50%;background:${warnHtml ? '#fffbeb' : '#f0fdf4'};border:1.5px solid ${warnHtml ? '#fde68a' : '#bbf7d0'};display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="${warnHtml ? '#f59e0b' : '#16a34a'}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          ${warnHtml
            ? `<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
               <line x1="12" y1="9" x2="12" y2="13"/>
               <line x1="12" y1="17" x2="12.01" y2="17"/>`
            : `<polyline points="20 6 9 17 4 12"/>`
          }
        </svg>
      </div>
      <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Approve leave?</p>
      <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0 0 ${warnHtml ? '0.75rem' : '0'}">
        Confirm approval for <strong>${item.name}</strong>'s ${item.type} request lasting ${item.duration}.
      </p>
      ${warnHtml ? `<div style="font-size:12px;color:#b45309;background:#fef9c3;border:1px solid #fde68a;border-radius:8px;padding:8px 10px;text-align:left">${warnHtml}</div>` : ''}
    </div>
  `,
  width: '340px',
  showCancelButton: true,
  confirmButtonText: 'Yes, approve',
  cancelButtonText: 'Cancel',
  confirmButtonColor: '#111111',
  customClass: {
    popup: 'swal-employee rounded-2xl',
    actions: 'gap-2',
    confirmButton: 'rounded-lg text-sm font-medium px-5 py-2.5 bg-green-600 text-white hover:bg-green-700',
    cancelButton: 'rounded-lg text-sm font-medium px-5 py-2.5 bg-white text-gray-500 border border-gray-200 hover:bg-gray-50',
  }
});

    if (result.isConfirmed) {
      await doAction('approve', item.id, 'Approved by manager');
    }
  };

  /* ── approve cancellation ────────────────────────────── */
  const approveCancellation = async (item) => {
   const result = await Swal.fire({
  html: `
    <div style="padding: 1rem 0.5rem">
      <div style="width:56px;height:56px;border-radius:50%;background:#fffbeb;border:1.5px solid #fde68a;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
      <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Approve cancellation?</p>
      <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">
        Approve cancellation for <strong>${item.name}</strong>'s ${item.type}? This will cancel the record and restore their profile balance.
      </p>
    </div>
  `,
  width: '340px',
  showCancelButton: true,
  confirmButtonText: 'Yes, cancel leave',
  cancelButtonText: 'Dismiss',
  confirmButtonColor: '#111111',
  customClass: {
    popup: 'swal-employee rounded-2xl',
    actions: 'gap-2',
    confirmButton: 'rounded-lg text-sm font-medium px-5 py-2.5 bg-orange-500 text-white hover:bg-orange-600',
    cancelButton: 'rounded-lg text-sm font-medium px-5 py-2.5 bg-white text-gray-500 border border-gray-200 hover:bg-gray-50',
  }
});

    if (result.isConfirmed) {
      doCancelAction('approve', item.id, 'Cancellation approved by admin');
    }
  };

  /* ── reject flows ────────────────────────────────────── */
  const rejectClick       = (item) => { setSelItem(item); setModalOpen(true); };
  const rejectCancellation = (item) => { setSelItem({ ...item, isCancelReq: true }); setModalOpen(true); };

  const rejectConfirm = async () => {
    if (!comment.trim()) { 
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#fffbeb;border:1.5px solid #fde68a;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Required field</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">Please provide a valid reason text block before submitting rejection details.</p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'OK',
      confirmButtonColor: '#111111',
      customClass: {
        popup: 'swal-employee rounded-2xl',
        confirmButton: 'rounded-sm text-sm font-medium px-6',
      }
    });
      return; 
    }
    if (selItem.isCancelReq) {
      await doCancelAction('reject', selItem.id, comment.trim());
    } else {
      await doAction('reject', selItem.id, comment.trim());
    }
    setComment(''); setModalOpen(false); setSelItem(null);
  };

  const closeModal = () => { setComment(''); setModalOpen(false); setSelItem(null); };

  /* ── filter ──────────────────────────────────────────── */
  const parseCardDate = (dateStr) => {
    if (!dateStr || dateStr === 'Not specified') return null;
    const parts = dateStr.split(/\s*[-–]\s*/).map(s => s.trim());
    const toISO = (s) => {
      if (!s) return null;
      if (s.includes('/')) {
        const [d, m, y] = s.split('/');
        return y && m && d ? `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}` : null;
      }
      return s.slice(0, 10);
    };
    return { start: toISO(parts[0]), end: toISO(parts[1] || parts[0]) };
  };

  const shown = cards.filter(c => {
    const aliasHit = TYPE_FILTER_ALIASES[filterType]?.some(alias =>
      c.type.toLowerCase().includes(alias.toLowerCase())
    );
    const okType   = filterType === 'All Types' ||
      c.type.toLowerCase().includes(filterType.toLowerCase()) ||
      aliasHit;
    const s = search.toLowerCase();
    const okSearch = c.name.toLowerCase().includes(s) ||
                     c.email.toLowerCase().includes(s) ||
                     c.reason.toLowerCase().includes(s) ||
                     c.type.toLowerCase().includes(s) ||
                     (!!s && 'earned'.includes(s) && c.type.toLowerCase().includes('casual'));

    let okDate = true;
    if (dateFrom || dateTo) {
      const range = parseCardDate(c.dates);
      if (!range) {
        okDate = false;
      } else {
        if (dateFrom && range.end   && range.end   < dateFrom) okDate = false;
        if (dateTo   && range.start && range.start > dateTo)   okDate = false;
      }
    }

    return okType && okSearch && okDate;
  });

  const isMedical = (type) => type === 'Medical Leave' || type.toLowerCase().includes('medical');

  const btnLabel = (itemId, action, defaultLabel) =>
    processingItem?.id === itemId && processingItem?.action === action
      ? 'Processing…'
      : defaultLabel;

  return (
    <div className="approval-main">
      <div className="mdmanager-navbar" style={{ marginTop:'1px', overflowX:'hidden' }}><Navbar /></div>

      <div className="manager-approval-mainbar" style={{ width:'100%', overflowX:'hidden' }}>

        <TopBar currentUser={currentUser} profilePic={profilePic} />

        <div className="approvals-container">

          <div className="approvals-page-header">
            <div className="approvals-title-block">
              <h1>Pending Approvals</h1>
              <p>Review and action leave requests from your team</p>
            </div>
            <div className="approvals-controls">
              {!loading && !error && (
                <span className="approvals-count-badge">
                  <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.8"/>
                  </svg>
                  {shown.length} pending
                </span>
              )}
              <input
                type="text"
                className="search-bar-manag-team"
                placeholder="Search name, email, leave type..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <select className="filter-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option>All Types</option>
                <option>Earned Leave</option>
                <option>Medical</option>
                <option>Permission</option>
              </select>
              
              <style>{`
                .date-input-fake-wrap { position: relative; }
                .date-input-fake-wrap input[type="date"] {
                  opacity: 0;
                  position: relative;
                  z-index: 2;
                  width: 100%;
                  height: 100%;
                  cursor: pointer;
                }
                .date-input-fake-display {
                  position: absolute;
                  inset: 0;
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  gap: 6px;
                  padding: 0 10px;
                  pointer-events: none;
                  z-index: 1;
                  font-size: 13px;
                  white-space: nowrap;
                  overflow: hidden;
                }
                .date-input-fake-display svg {
                  color: #6b7280;
                  flex-shrink: 0;
                }
              `}</style>
              <div className="date-range-filter">
                <div className="date-range-input-wrap">
                  <div className="date-input-wrap date-input-fake-wrap">
                    <div className="date-input-fake-display" style={{ color: dateFrom ? '#111827' : '#9ca3af' }}>
                      <span>{dateFrom ? fmt(dateFrom) : 'Leave Start'}</span>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24">
                        <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/>
                        <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <input
                      type="date"
                      className="date-filter-input"
                      value={dateFrom}
                      onChange={e => setDateFrom(e.target.value)}
                    />
                  </div>
                </div>
                <span className="date-range-sep">→</span>
                <div className="date-range-input-wrap">
                  <div className="date-input-wrap date-input-fake-wrap">
                    <div className="date-input-fake-display" style={{ color: dateTo ? '#111827' : '#9ca3af' }}>
                      <span>{dateTo ? fmt(dateTo) : 'Leave End'}</span>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24">
                        <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/>
                        <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <input
                      type="date"
                      className="date-filter-input"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={e => setDateTo(e.target.value)}
                    />
                  </div>
                </div>
                <button
                  className={`date-clear-btn${(dateFrom || dateTo) ? '' : ' date-clear-btn--hidden'}`}
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  title="Clear date filter"
                  tabIndex={(dateFrom || dateTo) ? 0 : -1}
                >
                  <svg width="12" height="12" fill="none" viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {loading && (
            <div className="approvals-state-box">
              <p>Loading pending approvals…</p>
            </div>
          )}

          {error && !loading && (
            <div className="approvals-state-box">
              <svg width="40" height="40" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="#d1d5db" strokeWidth="1.5"/>
                <path d="M12 8v4M12 16h.01" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <p>Failed to load</p>
              <small>{error}</small>
              <button className="retry-btn" onClick={load}>Retry</button>
            </div>
          )}

          {!loading && !error && shown.length === 0 && (
            <div className="approvals-state-box">
              <svg width="48" height="48" fill="none" viewBox="0 0 24 24">
                <path d="M9 11l3 3L22 4" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p>No pending requests</p>
              <small>{search || filterType !== 'All Types' || dateFrom || dateTo ? 'Try adjusting your search or filters' : 'All requests have been processed'}</small>
            </div>
          )}

          {!loading && !error && shown.map((item, idx) => (
            <div
              key={`${item.id}-${idx}`}
              className="approval-card"
              style={item.isCancelReq ? {
                borderLeft: '4px solid #f97316',
                background: 'linear-gradient(to right, #fff7ed 0%, #ffffff 60px)'
              } : {}}
            >

              <div className="acard-top">
                <div className="acard-person">
                  <img
                    src={item.pic}
                    alt={item.name}
                    className="acard-avatar"
                    onError={e => { e.target.src = '/Images/Manurevalogo.png'; }}
                  />
                  <div className="acard-name-block">
                    <div className="acard-name">{item.name}</div>
                    <div className="acard-email">{item.email}</div>
                    {item.isTeam && (
                      <div className="acard-team-badge">
                        <svg width="10" height="10" fill="none" viewBox="0 0 24 24">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Team Member
                      </div>
                    )}
                  </div>
                </div>
                <div className="acard-top-right">
                  <span className="acard-date">Applied: {item.date}</span>
                  {item.isCancelReq ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: '#fff7ed', color: '#c2410c',
                      border: '1px solid #fed7aa', borderRadius: 6,
                      padding: '3px 10px', fontSize: 11, fontWeight: 700,
                      letterSpacing: 0.5
                    }}>
                      <svg width="11" height="11" fill="none" viewBox="0 0 24 24">
                        <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" strokeWidth="1.8"/>
                      </svg>
                      CANCEL REQUEST
                    </span>
                  ) : (
                    <span className={`priority-badge ${item.prio}`}>
                      {item.prio.charAt(0).toUpperCase() + item.prio.slice(1)} Priority
                    </span>
                  )}
                </div>
              </div>

              {item.isCancelReq && (
                <div style={{
                  margin: '0 0 14px 0',
                  padding: '10px 14px',
                  background: '#fff7ed',
                  border: '1px solid #fed7aa',
                  borderRadius: 8,
                  fontSize: 12,
                  color: '#92400e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" stroke="#f97316" strokeWidth="1.8"/>
                    <path d="M12 8v4M12 16h.01" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>
                    This employee has requested to <strong>cancel their approved leave</strong>.
                    Approving will cancel the leave and restore their balance.
                    Rejecting will keep the leave as approved.
                  </span>
                </div>
              )}

              <div className="acard-fields">

                <div className="acard-field">
                  <div className="acard-field-lbl">Leave Type</div>
                  <div className="acard-field-val">
                    {item.type.split(" ")[0].toLowerCase() === 'casual' ? 'Earned' : item.type.split(" ")[0]}
                  </div>
                </div>

                <div className="acard-field">
                  <div className="acard-field-lbl">Duration</div>
                  <div className="acard-field-val">
                    {item.duration}
                    {item.isHalf && <span className="halfday-tag">Half Day</span>}
                    {item.isHalf && item.halfPeriod && (
                      <span className="period-tag">
                        {item.halfPeriod === 'morning' ? '🌅 Morning' : '🌆 Afternoon'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="acard-field">
                  <div className="acard-field-lbl">Date(s)</div>
                  <div className="acard-field-val">{item.dates}</div>
                </div>

                <div className="acard-field">
                  <div className="acard-field-lbl">Current Balance</div>
                  <div className="acard-field-val">
                    {item.balance?.replace(/\[LOP:[^\]]+\]/g, '').replace(/\[PENDING LOP:[^\]]+\]/g, '').trim()}
                    {item.balance?.includes('[LOP:') && !item.balance?.includes('[PENDING LOP:') && (
                      <span className="lop-badge">Loss of Pay: {item.balance?.match(/\[LOP:(.*?)\]/)?.[1]}</span>
                    )}
                    {item.balance?.includes('[PENDING LOP:') && (
                      <span className="lop-pending-badge">Pending LOP</span>
                    )}
                  </div>
                </div>

                <div className="acard-field" style={{ gridColumn: isMedical(item.type) ? 'auto' : 'span 2' }}>
                  <div className="acard-field-lbl">Reason</div>
                  <div className="acard-field-val reason-val">{item.reason}</div>
                </div>

                {isMedical(item.type) && (
                  <div className="acard-field">
                    <div className="acard-field-lbl">Medical Certificate</div>
                    <div className="acard-field-val">
                      <div className="cert-row">
                        {item.hasDoc ? (
                          <>
                            <span className="cert-yes">
                              <svg width="11" height="11" fill="none" viewBox="0 0 24 24">
                                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Uploaded
                            </span>
                            {item.docUrl && (
                              <button className="cert-dl-btn" onClick={() => download(item.docUrl, item.docName)}>
                                <svg width="11" height="11" fill="none" viewBox="0 0 24 24">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                View
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="cert-no">
                            <svg width="11" height="11" fill="none" viewBox="0 0 24 24">
                              <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" strokeWidth="1.8"/>
                            </svg>
                            No document uploaded
                          </span>
                        )}
                      </div>
                      {item.docName && item.hasDoc && (
                        <div className="cert-filename">File: {item.docName}</div>
                      )}
                    </div>
                  </div>
                )}

              </div>

              <div className="acard-footer">
                {item.isCancelReq ? (
                  <>
                    <button
                      className="btn-approve"
                      onClick={() => approveCancellation(item)}
                      disabled={isAnyProcessing}
                      style={{
                        background: processingItem?.id === item.id && processingItem?.action === 'approve'
                          ? '#9ca3af'
                          : 'linear-gradient(135deg, #f97316, #ea580c)',
                        boxShadow: '0 4px 12px rgba(249,115,22,0.35)'
                      }}
                    >
                      <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {btnLabel(item.id, 'approve', 'Approve Cancel')}
                    </button>

                    <button
                      className="btn-reject"
                      onClick={() => rejectCancellation(item)}
                      disabled={isAnyProcessing}
                    >
                      <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      {btnLabel(item.id, 'reject', 'Keep Leave')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn-approve"
                      onClick={() => approve(item)}
                      disabled={isAnyProcessing}
                    >
                      <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {btnLabel(item.id, 'approve', 'Approve')}
                    </button>

                    <button
                      className="btn-reject"
                      onClick={() => rejectClick(item)}
                      disabled={isAnyProcessing}
                    >
                      <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      {btnLabel(item.id, 'reject', 'Reject')}
                    </button>
                  </>
                )}
              </div>

            </div>
          ))}

        </div>
      </div>

      {modalOpen && selItem && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h2>{selItem.isCancelReq ? 'Reject Cancellation Request' : 'Reject Leave Request'}</h2>
              <button className="close-button" onClick={closeModal}>×</button>
            </div>
            <div className="modal-content">

              {selItem.isCancelReq && (
                <div style={{
                  padding: '10px 14px', marginBottom: 16,
                  background: '#fff7ed', border: '1px solid #fed7aa',
                  borderRadius: 8, fontSize: 12, color: '#92400e'
                }}>
                  Rejecting this will <strong>keep the leave as approved</strong> — the employee's leave will NOT be cancelled.
                </div>
              )}

              <div className="modal-info-box">
                <strong>Employee:</strong> {selItem.name}<br/>
                <strong>Email:</strong> {selItem.email}<br/>
                <strong>Leave Type:</strong> {selItem.type}<br/>
                <strong>Duration:</strong> {selItem.duration}
                {selItem.isHalf && <span className="halfday-tag" style={{ marginLeft:8 }}>Half Day</span>}<br/>
                <strong>Dates:</strong> {selItem.dates}
              </div>

              <div className="form-group">
                <label>
                  {selItem.isCancelReq
                    ? <>Reason for keeping the leave <span style={{ color:'#ef4444' }}>*</span></>
                    : <>Reason for rejection <span style={{ color:'#ef4444' }}>*</span></>
                  }
                </label>
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder={
                    selItem.isCancelReq
                      ? 'Please provide a reason for keeping this leave as approved…'
                      : 'Please provide a detailed reason for rejecting this request…'
                  }
                  rows={5}
                  maxLength={500}
                />
                <div className="char-count">{comment.length}/500 characters</div>
              </div>

              <div className="modal-actions">
                <button
                  className="reject-request-btn"
                  onClick={rejectConfirm}
                  disabled={!comment.trim() || isAnyProcessing}
                  style={selItem.isCancelReq ? {
                    background: 'linear-gradient(135deg, #f97316, #ea580c)'
                  } : {}}
                >
                  {isAnyProcessing
                    ? 'Processing…'
                    : selItem.isCancelReq
                      ? 'Keep Leave Approved'
                      : 'Reject Request'
                  }
                </button>
                <button className="cancel-btn" onClick={closeModal} disabled={isAnyProcessing}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Approvals;